import { usePersist } from "../../lib/usePersist";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert, Loader2, History, Undo2 } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const BLOCKING = ["prices", "info", "headings", "banned"];

export const StyleCheckPanel = ({ kit, reload }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => http.get(`/kits/${kit.id}/style-check`).then((r) => setD(r.data)).catch(() => {}), [kit.id]);
  useEffect(() => { load(); }, [load, kit.updated_at]);
  const fix = async (key) => {
    setBusy(key);
    try { await http.post(`/kits/${kit.id}/style-check/fix/${key}`); toast.success("Fixing — this list updates as it finishes"); setTimeout(() => { load(); reload(); }, 3000); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  if (!d) return null;
  return (
    <section data-testid="style-check-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex items-center gap-2">
        {d.passed ? <CheckCircle2 size={18} className="text-emerald-600" /> : <ShieldAlert size={18} className="text-red-600" />}
        <b className="text-[#2B2A26]">House-style check</b>
        <span data-testid="style-check-result" className={`text-sm ${d.passed ? "text-emerald-700" : "text-red-600"}`}>
          {d.passed ? "Passed" : `${d.blocking} issue(s) worth fixing — this is a warning, publishing is still allowed`}
        </span>
      </div>
      {d.issues.length > 0 && (
        <ul className="mt-2">
          {d.issues.map((i) => (
            <li key={i.key} data-testid={`style-issue-${i.key}`} className="flex flex-wrap items-center gap-2 border-t border-stone-100 py-1.5 text-sm">
              <span className={`text-[10px] font-bold uppercase tracking-wider ${BLOCKING.includes(i.key) ? "text-red-600" : "text-stone-400"}`}>{BLOCKING.includes(i.key) ? "Must fix" : "Advice"}</span>
              <b className="text-[#2B2A26]">{i.label}</b>
              <span className="text-stone-600">{i.detail}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-stone-400">{i.where.join(" · ")}</span>
              {i.fix && (
                <button data-testid={`style-fix-${i.key}`} disabled={!!busy} onClick={() => (i.fix === "headings" || window.confirm(`${i.label}: this fix uses AI credits (roughly $0.01–$0.05 per page). Go ahead?`)) && fix(i.key)}
                  className="inline-flex items-center gap-1 rounded-sm bg-[#2B2A26] px-3 py-1 text-xs font-bold text-white hover:bg-black disabled:opacity-50">
                  {busy === i.key && <Loader2 size={11} className="animate-spin" />} {i.fix === "headings" ? "Fix" : i.fix === "costs" ? "Look up (uses AI)" : "Fix (uses AI)"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export const FixAllPanel = () => {
  const [d, setD] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/ops/fix-all").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d.status !== "running") return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [d.status, load]);
  const go = async () => {
    if (!window.confirm("Fix every kit that fails the house-style check? This uses AI credits and stops by itself below $10.")) return;
    setBusy(true);
    try { const r = await http.post("/ops/fix-all"); toast.success(r.data.kits ? `Fixing ${r.data.kits} kit(s), one at a time` : "Every kit already passes"); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const running = d.status === "running";
  const res = d.results || [];
  return (
    <section data-testid="fix-all-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <CheckCircle2 size={18} className="text-[#9A7B2C]" />
        <b className="text-[#2B2A26]">Fix kits before publish</b>
        <span data-testid="fix-all-status" className="text-sm text-stone-500">
          {running ? `Kit ${(d.done || 0) + 1} of ${d.total}${d.current ? ` · ${d.current}` : ""}${d.note ? ` · ${d.note}` : ""}`
            : d.status === "paused" ? d.note : d.status === "done" ? `Last run: ${res.filter((r) => !r.still.length).length} of ${d.total} kit(s) now pass`
            : "Completes info boxes, rewrites pages with banned words, matches heading capitals and moves prices, on every failing kit"}
        </span>
        <button data-testid="fix-all-btn" disabled={busy || running} onClick={go} className="ml-auto inline-flex items-center gap-1.5 rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">
          {(busy || running) && <Loader2 size={12} className="animate-spin" />} Fix all failing kits
        </button>
      </div>
      {res.length > 0 && (
        <ul className="mt-2 max-h-48 overflow-y-auto text-sm">
          {res.map((r, i) => <li key={r.id + i} data-testid={`fix-all-result-${i}`} className="border-t border-stone-100 py-1"><b className="text-[#2B2A26]">{r.destination}</b> <span className={r.still.length ? "text-red-600" : "text-emerald-700"}>{r.still.length ? `still: ${r.still.join(", ")}` : "passes"}</span></li>)}
        </ul>
      )}
    </section>
  );
};

export const HistoryPanel = ({ kit, reload }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = usePersist("kitops", false);
  const load = useCallback(() => http.get(`/kits/${kit.id}/history`).then((r) => setItems(r.data.items)).catch(() => {}), [kit.id]);
  useEffect(() => { load(); }, [load, kit.updated_at]);
  const undo = async (at, label) => {
    try { await http.post(`/kits/${kit.id}/history/undo?at=${encodeURIComponent(at)}`); toast.success(`Undid: ${label}`); load(); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!items.length) return null;
  return (
    <section data-testid="history-panel" className="mb-6 rounded-lg border border-stone-200 bg-white p-4">
      <button data-testid="history-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left">
        <History size={18} className="text-[#9A7B2C]" /><b className="text-[#2B2A26]">Change history</b>
        <span className="text-sm text-stone-500">{items.length} automatic change(s) · each can be undone</span>
      </button>
      {open && (
        <ul className="mt-2">
          {items.map((h, i) => (
            <li key={h.at} data-testid={`history-item-${i}`} className="flex items-center gap-3 border-t border-stone-100 py-1.5 text-sm">
              <span className="w-40 text-xs text-stone-400">{new Date(h.at).toLocaleString()}</span>
              <span className="flex-1 text-[#2B2A26]">{h.label}{h.undone ? <span className="ml-2 text-xs text-stone-400">(undone)</span> : null}</span>
              <button data-testid={`history-undo-${i}`} onClick={() => undo(h.at, h.label)} className="inline-flex items-center gap-1 text-xs font-bold text-[#9A7B2C] hover:underline"><Undo2 size={12} /> Undo</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
